// 📁 src/pages/StrategyMarketForm.js

import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import { 
  TextField, 
  MenuItem, 
  InputAdornment, 
  FormControl, 
  InputLabel, 
  Select,
  useMediaQuery
} from "@mui/material";
import axiosInstance from "api/axiosInstance";
import StrategyKPICharts from "./StrategyKPICharts";
import { LanguageContext } from "../../../../context/LanguageContext";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { COLORS } from "theme/tokens";

export default function StrategyMarketForm() {
  const theme = useTheme();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [ kpiOpen, setKpiOpen] = useState(false);
  const [rentingId, setRentingId] = useState(null);
  const [, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingRentStrategyId, setPendingRentStrategyId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // 🔍 搜尋與篩選狀態
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSymbol, setFilterSymbol] = useState("ALL");
  const [filterTimeframe, setFilterTimeframe] = useState("ALL");
  const [filterOrderType, ] = useState("ALL");
  const [sortBy, setSortBy] = useState("NEWEST");
  const [symbolOptions, setSymbolOptions] = useState(["ALL"]);
  const [timeframeOptions, setTimeframeOptions] = useState(["ALL"]);

  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  
  useEffect(() => {
    loadLanguageFile("StrategyMarketForm", "strategy/StrategyMarketForm/StrategyMarketForm_lang");
  }, [language, loadLanguageFile]);
  
  const LANGUAGE = useMemo(() => {
    return componentLanguageData.StrategyMarketForm || { Main: {}, Error: {} };
  }, [componentLanguageData]);

  const handleRentStrategy = (strategyId) => {
    setPendingRentStrategyId(strategyId);
    setConfirmDialogOpen(true);
  };

  const confirmRent = async () => {
    setRentingId(pendingRentStrategyId);
    try {
      const response = await axiosInstance.post("/strategy/rent", {
        strategyId: pendingRentStrategyId
      });

      // ✅ 若回傳 paymentHtml，表示需跳轉綠界付款
      if (response.data.paymentHtml) {
        const div = document.createElement("div");
        div.innerHTML = response.data.paymentHtml;
        document.body.appendChild(div);
        // 確保表單送出
        const form = div.querySelector("form");
        if (form) {
          form.submit();
        }
        return; 
      }

      setSnackbar({ open: true, message: LANGUAGE.Alert.Content1, severity: "success" });
      // If no paymentHtml, assume success and refresh strategies or redirect
      // Original logic was to redirect, now we refresh strategies if paymentHtml is not present
      fetchStrategies(); 
      // Original redirect logic:
      // setTimeout(() => {
      //   window.location.href = "/strategy-manager";
      // }, 1000);

    } catch (err) {
      console.error(LANGUAGE.Error.Content2, err); // Changed from Content10 to Content2 to match existing error key
      setSnackbar({ open: true, message: LANGUAGE.Error.Content2, severity: "error" }); // Changed from Content10 to Content2
    } finally {
      setConfirmDialogOpen(false);
      setPendingRentStrategyId(null);
      setRentingId(null);
    }
  };

  const fetchStrategies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/strategy/available-for-lease");
      setStrategies(res.data);
    } catch (err) {
      console.error(LANGUAGE.Error.Content3, err);
    } finally {
      setLoading(false);
    }
  }, [LANGUAGE.Error.Content3]);

  const handleOpenKPI = (rawReport) => {
    try {
      const report = typeof rawReport === "string" ? JSON.parse(rawReport) : rawReport;
      // console.log("report:", report);
      setSelectedReport(report);
      setKpiOpen(true);
    } catch (e) {
      console.error(LANGUAGE.Error.Content4, e);
    }
  };

  const handleCloseKPI = () => {
    setKpiOpen(false);
    setSelectedReport(null);
  };

  useEffect(() => {
    fetchStrategies();
    
    // ✅ 取得動態選單選項
    const fetchOptions = async () => {
      try {
        const res = await axiosInstance.get("/strategy/options");
        setSymbolOptions(["ALL", ...res.data.symbols.map(s => s.value)]);
        setTimeframeOptions(["ALL", ...res.data.timeframes.map(t => t.value)]);
      } catch (err) {
        console.error("Failed to fetch options:", err);
      }
    };
    fetchOptions();

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const userId = user.id || user.userId || user.uid;
        setCurrentUserId(userId);
      } catch (e) {
        console.error("Parse user error", e);
      }
    }
  }, [fetchStrategies]);

  // ✅ 核心過濾與排序邏輯
  const filteredStrategies = strategies
    .filter((s) => {
      const matchesSearch = 
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.symbol.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSymbol = filterSymbol === "ALL" || s.symbol === filterSymbol;
      const matchesTimeframe = filterTimeframe === "ALL" || s.timeframe === filterTimeframe;
      const matchesOrderType = filterOrderType === "ALL" || s.ordertype === filterOrderType;
      return matchesSearch && matchesSymbol && matchesTimeframe && matchesOrderType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "RETURN_DSC":
          return (b.totalReturn || 0) - (a.totalReturn || 0);
        case "WINRATE_DSC":
          return (b.winRate || 0) - (a.winRate || 0);
        case "PRICE_ASC":
          const priceA = a.leasingSetting?.discountPrice || a.leasingSetting?.price || 0;
          const priceB = b.leasingSetting?.discountPrice || b.leasingSetting?.price || 0;
          return priceA - priceB;
        case "NEWEST":
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  // 同步策略回測產生器的固定選項
  // 動態取得選單選項
  const SYMBOL_OPTIONS = symbolOptions;
  const TIMEFRAME_OPTIONS = timeframeOptions;

  const renderStrategyCard = (strategy, idx) => {
    const rawJson = strategy.reportJson;
    let winRate = null, totalReturnRate = null;

    try {
      const parsed = typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
      winRate = parsed?.performance_metrics?.win_rate;
      totalReturnRate = parsed?.performance_metrics?.total_return_rate;
    } catch (e) {
      console.error(LANGUAGE.Error.Content5, e);
    }

    return (
      <Box position="relative">
        {/* Discount Countdown Badge */}
        {strategy.leasingSetting?.discountPrice && strategy.leasingSetting?.discountEnd && (() => {
          const now = new Date();
          const end = new Date(strategy.leasingSetting.discountEnd);
          const diffMs = end.getTime() - now.getTime();
          const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          return diffDays > 0 ? (
            <Chip
              label={`${LANGUAGE.Main.Content1} ${diffDays} ${LANGUAGE.Main.Content2}`}
              size="small"
              sx={{
                position: "absolute",
                top: -8,
                right: 16,
                zIndex: 2,
                fontWeight: "bold",
                background: "linear-gradient(135deg, #f44336, #e53935)",
                color: COLORS.white,
                boxShadow: "0 2px 6px rgba(244,67,54,0.4)",
              }}
            />
          ) : null;
        })()}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            transition: "all 0.3s ease",
            '&:hover': {
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
              transform: 'translateY(-2px)',
              borderLeftColor: theme.palette.secondary.main,
            },
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            {/* Header: Title + Tags + View Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box flex={1}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <AssessmentIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <MDTypography variant="h6" fontWeight="bold" sx={{ fontSize: "1.1rem" }}>
                    {strategy.title}
                  </MDTypography>
                </Stack>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  <Chip
                    icon={<CurrencyExchangeIcon sx={{ fontSize: 14 }} />}
                    label={strategy.symbol}
                    size="small"
                    sx={{ 
                      height: 22,
                      fontSize: "0.7rem",
                      bgcolor: alpha(theme.palette.info.main, 0.1), 
                      color: theme.palette.info.main 
                    }}
                  />
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                    label={strategy.timeframe}
                    size="small"
                    sx={{ 
                      height: 22,
                      fontSize: "0.7rem",
                      bgcolor: alpha(theme.palette.warning.main, 0.1), 
                      color: theme.palette.warning.main 
                    }}
                  />
                  <Chip
                    icon={<SmartphoneOutlinedIcon sx={{ fontSize: 14 }} />}
                    label={strategy.ordertype}
                    size="small"
                    sx={{ 
                      height: 22,
                      fontSize: "0.7rem",
                      bgcolor: alpha(theme.palette.success.main, 0.1), 
                      color: theme.palette.success.main 
                    }}
                  />
                </Stack>
              </Box>
              
              <Tooltip title={LANGUAGE.Main.Content12} arrow>
                <IconButton 
                  onClick={() => handleOpenKPI(strategy.reportJson)}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    }
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Performance Metrics - Prominent Display */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={4}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    minHeight: 80,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    bgcolor: totalReturnRate > 0 
                      ? alpha(theme.palette.success.main, 0.08) 
                      : alpha(theme.palette.error.main, 0.08),
                    border: `1px solid ${totalReturnRate > 0 
                      ? alpha(theme.palette.success.main, 0.3) 
                      : alpha(theme.palette.error.main, 0.3)}`,
                    textAlign: "center",
                  }}
                >
                  <MDTypography variant="caption" color="text" sx={{ opacity: 0.7, fontSize: "0.75rem", display: "block", mb: 0.5, whiteSpace: "nowrap" }}>
                    {LANGUAGE.Main.Content6}
                  </MDTypography>
                  <MDTypography
                    variant="h5"
                    fontWeight="bold"
                    color={
                      totalReturnRate > 0
                        ? "success"
                        : totalReturnRate < 0
                        ? "error"
                        : "text"
                    }
                  >
                    {totalReturnRate != null ? `${totalReturnRate.toFixed(2)}%` : "—"}
                  </MDTypography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    minHeight: 80,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    bgcolor: winRate > 0 
                      ? alpha(theme.palette.success.main, 0.08) 
                      : alpha(theme.palette.error.main, 0.08),
                    border: `1px solid ${winRate > 0 
                      ? alpha(theme.palette.success.main, 0.3) 
                      : alpha(theme.palette.error.main, 0.3)}`,
                    textAlign: "center",
                  }}
                >
                  <MDTypography variant="caption" color="text" sx={{ opacity: 0.7, fontSize: "0.75rem", display: "block", mb: 0.5, whiteSpace: "nowrap" }}>
                    {LANGUAGE.Main.Content7}
                  </MDTypography>
                  <MDTypography
                    variant="h5"
                    fontWeight="bold"
                    color={
                      winRate > 0
                        ? "success"
                        : winRate < 0
                        ? "error"
                        : "text"
                    }
                  >
                    {winRate != null ? `${winRate.toFixed(2)}%` : "—"}
                  </MDTypography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    minHeight: 80,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    bgcolor: strategy.leasingSetting?.discountPrice 
                      ? alpha(theme.palette.error.main, 0.08)
                      : alpha(theme.palette.grey[500], 0.08),
                    border: `1px solid ${strategy.leasingSetting?.discountPrice 
                      ? alpha(theme.palette.error.main, 0.3)
                      : alpha(theme.palette.grey[500], 0.3)}`,
                    textAlign: "center",
                  }}
                >
                  <MDTypography variant="caption" color="text" sx={{ opacity: 0.7, fontSize: "0.75rem", display: "block", mb: 0.5, whiteSpace: "nowrap" }}>
                    {strategy.leasingSetting?.discountPrice ? LANGUAGE.Main.Content13 : LANGUAGE.Main.Content15}
                  </MDTypography>
                  {strategy.leasingSetting?.discountPrice ? (
                    <Stack direction="row" alignItems="baseline" spacing={0.5} justifyContent="center" flexWrap="nowrap">
                      <MDTypography variant="h5" color="error" fontWeight="bold">
                        ${strategy.leasingSetting.discountPrice}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        sx={{
                          textDecoration: "line-through",
                          color: theme.palette.grey[500],
                          fontSize: "0.75rem",
                          opacity: 0.8
                        }}
                      >
                        ${strategy.leasingSetting.price}
                      </MDTypography>
                    </Stack>
                  ) : (
                    <MDTypography variant="h5" fontWeight="bold">
                      ${strategy.leasingSetting?.price ?? "—"}
                    </MDTypography>
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Action Button - Full Width */}
            { (strategy.isOwner || strategy.userId === currentUserId || strategy.owner?.id === currentUserId) ? (
              <MDButton
                variant="outlined"
                color="info"
                size="large"
                fullWidth
                disabled
                sx={{
                  py: 1.25,
                  fontSize: "0.95rem",
                  fontWeight: "bold",
                }}
              >
                <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                {LANGUAGE.Main.Content28}
              </MDButton>
            ) : (
              <MDButton
                variant="gradient"
                color="success"
                size="large"
                fullWidth
                onClick={() => handleRentStrategy(strategy.id)}
                disabled={rentingId === strategy.id}
                sx={{
                  py: 1.25,
                  fontSize: "0.95rem",
                  fontWeight: "bold",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                }}
              >
                {rentingId === strategy.id ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                    {LANGUAGE.Main.Content16}
                  </>
                )}
              </MDButton>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <MDBox p={3} sx={{ background: COLORS.grayLightest, minHeight: "100vh" }}>
      {/* Header Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 3,
          p: 3,
          mb: 3,
          color: "white",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <StorefrontIcon sx={{ fontSize: 40 }} />
          <Box>
            <MDTypography variant="h4" color="white" fontWeight="bold">
              {LANGUAGE.Main.Content17}
            </MDTypography>
            <MDTypography variant="body2" color="white" sx={{ opacity: 0.9, mt: 0.5 }}>
              {LANGUAGE.Main.Content27 || "Browse and rent high-performance trading strategies"}
            </MDTypography>
          </Box>
        </Stack>
      </Box>

      {/* 🔍 搜尋與篩選工具列 */}
      <Card sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Grid container spacing={2} alignItems="center">
          {/* 搜尋框 */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder={LANGUAGE.Main.Content31 || "Search..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
            />
          </Grid>

          {/* 分類篩選 */}
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{LANGUAGE.Main.Content5 || "Symbol"}</InputLabel>
              <Select
                value={filterSymbol}
                label={LANGUAGE.Main.Content5 || "Symbol"}
                onChange={(e) => setFilterSymbol(e.target.value)}
                sx={{ borderRadius: 2, height: 45 }}
              >
                {SYMBOL_OPTIONS.map((sym) => (
                  <MenuItem key={sym} value={sym}>
                    {sym === "ALL" ? (LANGUAGE.Main.Content34 || "All Symbols") : sym}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{LANGUAGE.Main.Content9 || "Timeframe"}</InputLabel>
              <Select
                value={filterTimeframe}
                label={LANGUAGE.Main.Content9 || "Timeframe"}
                onChange={(e) => setFilterTimeframe(e.target.value)}
                sx={{ borderRadius: 2, height: 45 }}
              >
                {TIMEFRAME_OPTIONS.map((tf) => (
                  <MenuItem key={tf} value={tf}>
                    {tf === "ALL" ? (LANGUAGE.Main.Content35 || "All Timeframes") : tf}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* 排序 */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{LANGUAGE.Main.Content33 || "Sort By"}</InputLabel>
              <Select
                value={sortBy}
                label={LANGUAGE.Main.Content33 || "Sort By"}
                onChange={(e) => setSortBy(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" sx={{ ml: 1 }} />
                  </InputAdornment>
                }
                sx={{ borderRadius: 2, height: 45 }}
              >
                <MenuItem value="NEWEST">{LANGUAGE.Main.Content40 || "Newest"}</MenuItem>
                <MenuItem value="RETURN_DSC">{LANGUAGE.Main.Content37 || "Best Return"}</MenuItem>
                <MenuItem value="WINRATE_DSC">{LANGUAGE.Main.Content38 || "High Win Rate"}</MenuItem>
                <MenuItem value="PRICE_ASC">{LANGUAGE.Main.Content39 || "Price: Low to High"}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
          <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
          <MDTypography variant="h6" color="text" sx={{ mt: 2 }}>
            {LANGUAGE.Main.Content29}
          </MDTypography>
        </Box>
      ) : filteredStrategies.length === 0 ? (
        <Card sx={{ p: 5, textAlign: "center", borderRadius: 3 }}>
          <SearchIcon sx={{ fontSize: 80, color: theme.palette.grey[400], mb: 2 }} />
          <MDTypography variant="h5" color="text" fontWeight="medium" gutterBottom>
            {LANGUAGE.Main.Content18}
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ opacity: 0.7 }}>
            {LANGUAGE.Main.Content30}
          </MDTypography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredStrategies.map((strategy, idx) => (
            <Zoom in key={strategy.id} style={{ transitionDelay: `${idx * 75}ms` }}>
              <Grid item xs={12}>{renderStrategyCard(strategy, idx)}</Grid>
            </Zoom>
          ))}
        </Grid>
      )}

      {/* KPI Dialog */}
      <Dialog 
        open={kpiOpen} 
        onClose={handleCloseKPI} 
        fullWidth 
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <MDTypography variant="h5" fontWeight="bold">
            {LANGUAGE.Main.Content19}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          {selectedReport ? <StrategyKPICharts data={selectedReport} /> : LANGUAGE.Main.Content26}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="text" color="secondary" onClick={handleCloseKPI}>
            {LANGUAGE.Main.Content20}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Confirm Rent Dialog */}
      <Dialog 
        open={confirmDialogOpen} 
        onClose={() => setConfirmDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon color="success" />
            <MDTypography variant="h5" fontWeight="bold">
              {LANGUAGE.Main.Content21}
            </MDTypography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">
            {LANGUAGE.Main.Content22}
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="text" color="secondary" onClick={() => setConfirmDialogOpen(false)}>
            {LANGUAGE.Main.Content23}
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            onClick={confirmRent}
          >
            {LANGUAGE.Main.Content24}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
